// hooks/useSpeechBridge.js
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { createSpeechBridge } from '../components/speechBridge';
import { Converter } from 'opencc-js';

export function useSpeechBridge() {
  const [transcript, setTranscript] = useState('');
  const [listening, setListening] = useState(false);
  const [error, setError] = useState(null);
  const finalRef = useRef('');   // 已确认文本
  const draftRef = useRef('');   // 临时文本（partial 预览）

  // --- 语言：更稳的获取方式（SSR 安全） ---
  const uiST = useMemo(() => {
    try {
      return (typeof window !== 'undefined' &&
        window.localStorage?.getItem('userLanguage') === 'T') ? 'T' : 'S';
    } catch { return 'S'; }
  }, []);

  const speechLang = useMemo(() => (uiST === 'T' ? 'zh-TW' : 'zh-CN'), [uiST]);

  // --- 简繁转换器（OpenCC） ---
  const toTW = useMemo(() => Converter({ from: 'cn', to: 'tw' }), []);
  const toCN = useMemo(() => Converter({ from: 'tw', to: 'cn' }), []);
  const normalize = useCallback(
    (txt) => (uiST === 'T' ? toTW(txt) : toCN(txt)),
    [uiST, toTW, toCN]
  );

  // --- 桥 ---
  const bridge = useMemo(() => createSpeechBridge({
    lang: speechLang,
    preferAzureOnAndroidChrome: true,      // ← 开启 Android Chrome 优先 Azure
    azure: {
      region: 'westus',                  // 和后端发 token 的 Region 一致
      tokenUrl: '/api/speech/token',       // 你后端的短期 token 接口
    },
    onPartial: (t) => {
      draftRef.current = normalize(t || '');
      // 预览 = 已确认 + 临时，不覆盖已确认
      setTranscript(finalRef.current + draftRef.current);
    },
    onFinal: (t) => {
      const commit = draftRef.current || normalize(t || '');
      // 避免重复：只有当“尾部不是 commit”时才追加
      if (commit && !finalRef.current.endsWith(commit)) {
        finalRef.current += commit;
      }
      draftRef.current = '';
      setTranscript(finalRef.current); // 仅显示已确认文本
    },
    onStateChange: ({ status, error: nextError }) => {
      setListening(status === 'starting' || status === 'listening');
      if (status === 'error') setError(nextError || new Error('speech-recognition-error'));
      else if (status === 'starting') setError(null);
    },
  }), [speechLang, normalize]);

  // 浏览器/原生支持检测
  const browserSupportsSpeechRecognition =
    typeof window !== 'undefined' && !Capacitor.isNativePlatform()
      ? Boolean(
          window.SpeechRecognition
          || window.webkitSpeechRecognition
          || (window.isSecureContext && navigator.mediaDevices?.getUserMedia)
        )
      : false;

  // 重置：记得把 draft 一并清掉
  const resetTranscript = useCallback(() => {
    finalRef.current = '';
    draftRef.current = '';
    setTranscript('');
  }, []);

  const startListening = useCallback(async () => {
    resetTranscript();
    setError(null);
    try {
      await bridge.start();  // 必须放在用户手势回调里调用
    } catch (e) {
      console.error(e);
      setListening(false);
      setError(e);
      throw e;
    }
  }, [bridge, resetTranscript]);

  const stopListening = useCallback(async () => {
    try {
      await bridge.stop();
    } finally {
      setListening(false);
    }
  }, [bridge]);

  // 卸载或 bridge 实例变更时清理
  useEffect(() => {
    return () => { bridge.destroy?.(); };
  }, [bridge]);

  return {
    transcript,
    listening,
    error,
    resetTranscript,
    browserSupportsSpeechRecognition,
    startListening,
    stopListening,
    isNative: Capacitor.isNativePlatform(),
  };
}
